import React from "react";

import { OUTCOME_BUNDLE, OUTCOME_JSON, OUTCOME_NONE, resolveSlotOutcome } from "../../utils/adConfig";
import { useAuctionOutcome } from "../../utils/adAuction";
import { SspAdapter } from "./SspAdapter";
import { YandexAdapter } from "./YandexAdapter";
import SmiWidget from "../smiWidgets/SmiWidget";
import MoeVideo from "../smiWidgets/MoeVideo";

/**
 * Единая точка входа рендера рекламного слота.
 *
 * Вызов знает только имя слота: провайдер, вариант и участие в ротации уже
 * решены — провайдер полем в БД, вариант резолвером, плечо ротации сервером.
 *
 * `render` нужен слотам, у которых компонент шаблона не сводится к адаптеру
 * провайдера: MoeVideo в разных шаблонах разный и принимает свои пропы. Тогда слот
 * по-прежнему решает, брать запись из БД или уйти в бандл, а рисует её шаблон сам.
 *
 * Пока кампания не перенесена в БД (или выключена флагом `is_active`), слот
 * рендерит `fallback` — существующий JSX шаблона. Так поведение при пустой БД
 * остаётся ровно текущим, включая семантику фолбэка на `main` у каждого шаблона.
 *
 * Запись с `auction` — участник аукциона RTB против соперника: она ждёт исхода и
 * рендерится, только если выиграла её роль. Шаблоны про аукцион не знают.
 */
export function AdSlot({ slot, isFull, isDesktop, fallback = null, json, render = null }) {
    const outcome = resolveSlotOutcome(slot, { isFull, isDesktop });

    if (outcome.kind === OUTCOME_BUNDLE) {
        return fallback;
    }

    // json-место рисует тизерный компонент шаблона; без пропа им считается fallback.
    if (outcome.kind === OUTCOME_JSON) {
        return json === undefined ? fallback : json;
    }

    // Слот не заведён или плечо ротации проиграло. В fallback уходить нельзя: это
    // бандл, а у проигравшего плеча — ещё и второе плечо рядом с выбранным.
    if (outcome.kind === OUTCOME_NONE) {
        return null;
    }

    const { entries } = outcome;

    return (
        <React.Fragment>
            {entries.map((entry, index) => {
                const content = render ? (
                    render(entry)
                ) : (
                    <AdEntry entry={entry} isFull={isFull} isDesktop={isDesktop} />
                );

                return entry.auction ? (
                    <AuctionGate key={`${slot}-${index}`} entry={entry}>
                        {content}
                    </AuctionGate>
                ) : (
                    <React.Fragment key={`${slot}-${index}`}>{content}</React.Fragment>
                );
            })}
        </React.Fragment>
    );
}

// До исхода и при проигрыше — ничего, в том числе без SDK провайдера и без ухода в
// fallback: проигравший участник — это то же, что проигравшее плечо ротации.
function AuctionGate({ entry, children }) {
    const outcome = useAuctionOutcome(entry.auction);

    if (!outcome || outcome.winner !== entry.auctionRole) {
        return null;
    }

    return children;
}

function AdEntry({ entry, isFull, isDesktop }) {
    if (entry.provider === "yandex") {
        return <YandexAdapter entry={entry} />;
    }

    // smi-блок в аукционе рисуется не как обычный информер, а кодом выигравшей ставки RTB.
    if (entry.auctionRole === "ssp") {
        return <SspAdapter entry={entry} />;
    }

    if (entry.provider === "moevideo") {
        return <MoeVideo placements={entry.blockId} isFull={isFull} isDesktop={isDesktop} />;
    }

    return <SmiWidget blockIds={entry.blockId} isFull={isFull} isDesktop={isDesktop} />;
}

export default AdSlot;
